import type { PricedPackage, PricedPackageItem, VpeLine } from './types';

/**
 * Recept-kost van 1 pakket: som van (qty_per_package × inkoopprijs) voor alle items.
 * Excludeert colli-rest (die zit in vpe-line.total_purchase_cost).
 */
export function calculateRecipeCost(items: PricedPackageItem[]): number {
  return items.reduce((sum, item) => {
    if (!item.gift_product) return sum;
    return sum + item.quantity_per_package * item.gift_product.unit_purchase_price;
  }, 0);
}

/**
 * Colli-overhead per pakket: het verschil tussen wat werkelijk ingekocht
 * wordt (incl rest) en het recept × aantal pakketten.
 */
export function calculateColliOverhead(
  items: PricedPackageItem[],
  vpeLines: VpeLine[],
  packageQuantity: number
): { total: number; per_package: number; leftover_value: number } {
  const recipePerPackage = calculateRecipeCost(items);
  const totalRecipeAcrossAll = recipePerPackage * packageQuantity;
  const totalActualPurchase = vpeLines.reduce((sum, l) => sum + l.total_purchase_cost, 0);
  const overhead = totalActualPurchase - totalRecipeAcrossAll;
  const leftoverValue = vpeLines.reduce((sum, l) => sum + l.leftover_value, 0);
  return {
    total: overhead,
    per_package: packageQuantity > 0 ? overhead / packageQuantity : 0,
    leftover_value: leftoverValue,
  };
}

/**
 * Verkoopprijs en marge gebaseerd op markup-model:
 * Mark stelt globaal margin_pct in. Verkoop = (inkoop + handling + transport) / (1 - margin/100).
 * Geen per-item verkoopprijs nodig in dit model.
 */
export interface MarkupSummary {
  total_purchase_cost: number;
  handling_cost: number;
  transport_cost: number;
  total_cost: number;
  margin_pct: number;
  selling_price_excl_btw: number;
  selling_price_excl_btw_per_package: number;
  margin_euros: number;
}

export function calculateMarkupSummary(
  pkg: Pick<PricedPackage, 'quantity' | 'handling_cost_per_package' | 'transport_cost_per_package'>,
  vpeLines: VpeLine[],
  marginPct: number
): MarkupSummary {
  if (marginPct < 0 || marginPct >= 100) {
    throw new Error('marginPct must be in [0, 100)');
  }
  const totalPurchaseCost = vpeLines.reduce((sum, l) => sum + l.total_purchase_cost, 0);
  const totalHandling = pkg.handling_cost_per_package * pkg.quantity;
  const totalTransport = pkg.transport_cost_per_package * pkg.quantity;
  const totalCost = totalPurchaseCost + totalHandling + totalTransport;
  const sellingExclBtw = totalCost / (1 - marginPct / 100);
  const sellingPerPackage = pkg.quantity > 0 ? sellingExclBtw / pkg.quantity : 0;
  const marginEuros = sellingExclBtw - totalCost;
  return {
    total_purchase_cost: totalPurchaseCost,
    handling_cost: totalHandling,
    transport_cost: totalTransport,
    total_cost: totalCost,
    margin_pct: marginPct,
    selling_price_excl_btw: sellingExclBtw,
    selling_price_excl_btw_per_package: sellingPerPackage,
    margin_euros: marginEuros,
  };
}

/** Past BTW toe per item-categorie en geeft incl-btw verkoop. */
export function applyBtwToSelling(
  vpeLines: VpeLine[],
  marginPct: number
): { selling_incl_btw: number; effective_btw_rate: number } {
  if (vpeLines.length === 0) {
    return { selling_incl_btw: 0, effective_btw_rate: 21 };
  }
  // Per regel: kost-deel × markup × (1 + btw)
  const factor = 1 / (1 - marginPct / 100);
  let totalIncl = 0;
  let totalExcl = 0;
  for (const l of vpeLines) {
    const cat = l.product.btw_category;
    const rate = cat === 'low' ? 9 : 21;
    const exclLine = l.total_purchase_cost * factor;
    totalExcl += exclLine;
    totalIncl += exclLine * (1 + rate / 100);
  }
  const effective = totalExcl > 0 ? ((totalIncl - totalExcl) / totalExcl) * 100 : 21;
  return { selling_incl_btw: totalIncl, effective_btw_rate: effective };
}

/**
 * Variant van applyBtwToSelling met losse marges per BTW-categorie.
 * Mark kan 9%-items met andere marge bepalen dan 21%-items.
 * - low (9%): food items
 * - high (21%): non-food + verpakking
 * Handling + transport worden naar rato van inkoop-aandeel over beide pots verdeeld.
 */
export interface SplitMarkupBreakdown {
  low: {
    purchase_cost: number;
    handling_cost: number;
    transport_cost: number;
    total_cost: number;
    margin_pct: number;
    selling_excl_btw: number;
    selling_incl_btw: number;
    margin_euros: number;
  };
  high: {
    purchase_cost: number;
    handling_cost: number;
    transport_cost: number;
    total_cost: number;
    margin_pct: number;
    selling_excl_btw: number;
    selling_incl_btw: number;
    margin_euros: number;
  };
  total: {
    purchase_cost: number;
    total_cost: number;
    selling_excl_btw: number;
    selling_incl_btw: number;
    margin_euros: number;
    /** Gewogen gemiddelde marge over beide pots, op verkoop excl-btw. */
    weighted_margin_pct: number;
    effective_btw_rate: number;
  };
}

export function calculateSplitMarkupSummary(
  pkg: Pick<PricedPackage, 'quantity' | 'handling_cost_per_package' | 'transport_cost_per_package'>,
  vpeLines: VpeLine[],
  marginLowPct: number,
  marginHighPct: number
): SplitMarkupBreakdown {
  if (marginLowPct < 0 || marginLowPct >= 100) {
    throw new Error('marginLowPct must be in [0, 100)');
  }
  if (marginHighPct < 0 || marginHighPct >= 100) {
    throw new Error('marginHighPct must be in [0, 100)');
  }

  const purchaseLow = vpeLines
    .filter((l) => l.product.btw_category === 'low')
    .reduce((s, l) => s + l.total_purchase_cost, 0);
  const purchaseHigh = vpeLines
    .filter((l) => l.product.btw_category === 'high')
    .reduce((s, l) => s + l.total_purchase_cost, 0);
  const purchaseTotal = purchaseLow + purchaseHigh;

  const handlingTotal = pkg.handling_cost_per_package * pkg.quantity;
  const transportTotal = pkg.transport_cost_per_package * pkg.quantity;

  // Verdeel handling + transport naar rato van inkoop. Bij 0 inkoop: alles op high (default).
  const lowShare = purchaseTotal > 0 ? purchaseLow / purchaseTotal : 0;
  const handlingLow = handlingTotal * lowShare;
  const handlingHigh = handlingTotal - handlingLow;
  const transportLow = transportTotal * lowShare;
  const transportHigh = transportTotal - transportLow;

  const costLow = purchaseLow + handlingLow + transportLow;
  const costHigh = purchaseHigh + handlingHigh + transportHigh;

  const sellingExclLow = costLow / (1 - marginLowPct / 100);
  const sellingExclHigh = costHigh / (1 - marginHighPct / 100);
  const sellingInclLow = sellingExclLow * 1.09;
  const sellingInclHigh = sellingExclHigh * 1.21;

  const marginEurosLow = sellingExclLow - costLow;
  const marginEurosHigh = sellingExclHigh - costHigh;

  const totalCost = costLow + costHigh;
  const totalSellingExcl = sellingExclLow + sellingExclHigh;
  const totalSellingIncl = sellingInclLow + sellingInclHigh;
  const totalMarginEuros = marginEurosLow + marginEurosHigh;

  const weightedMarginPct = totalSellingExcl > 0 ? (totalMarginEuros / totalSellingExcl) * 100 : 0;
  const effectiveBtwRate =
    totalSellingExcl > 0 ? ((totalSellingIncl - totalSellingExcl) / totalSellingExcl) * 100 : 21;

  return {
    low: {
      purchase_cost: purchaseLow,
      handling_cost: handlingLow,
      transport_cost: transportLow,
      total_cost: costLow,
      margin_pct: marginLowPct,
      selling_excl_btw: sellingExclLow,
      selling_incl_btw: sellingInclLow,
      margin_euros: marginEurosLow,
    },
    high: {
      purchase_cost: purchaseHigh,
      handling_cost: handlingHigh,
      transport_cost: transportHigh,
      total_cost: costHigh,
      margin_pct: marginHighPct,
      selling_excl_btw: sellingExclHigh,
      selling_incl_btw: sellingInclHigh,
      margin_euros: marginEurosHigh,
    },
    total: {
      purchase_cost: purchaseTotal,
      total_cost: totalCost,
      selling_excl_btw: totalSellingExcl,
      selling_incl_btw: totalSellingIncl,
      margin_euros: totalMarginEuros,
      weighted_margin_pct: weightedMarginPct,
      effective_btw_rate: effectiveBtwRate,
    },
  };
}
